/**
 * Attribuut-bouwer — gedeeld tussen schermen die eigen velden laten definiëren.
 *
 * Stond tot 2026-08-18 in `SubtypesSettings`. Toen dat scherm verdween met het
 * subtype-mechanisme moest deze mee, want het profiel-beheerscherm gebruikt 'm.
 * Bewust verplaatst in plaats van gekopieerd: twee bouwers die uit elkaar lopen
 * is precies de drift die deze codebase al genoeg heeft gehad.
 */
import type { AttributeSchema } from "@ae/domain";
import { useTranslation } from "react-i18next";
import { ld } from "./ledgerUi";

const ATTR_BASE_TYPES = ["string", "bilingual_string", "int", "bool", "date", "enum"] as const;
type AttrBase = (typeof ATTR_BASE_TYPES)[number];

export interface AttrDraft {
  name: string;
  labelNl: string;
  labelEn: string;
  base: AttrBase;
  required: boolean;
  options: string;
}

export function schemaToDraft(a: AttributeSchema): AttrDraft {
  const optional = a.type.endsWith("?");
  const rawBase = optional ? a.type.slice(0, -1) : a.type;
  const base = (ATTR_BASE_TYPES as readonly string[]).includes(rawBase)
    ? (rawBase as AttrBase)
    : "string";
  return {
    name: a.name,
    labelNl: a.label?.nl ?? "",
    labelEn: a.label?.en ?? "",
    base,
    required: !optional,
    options: a.options?.join(", ") ?? "",
  };
}

export function draftToSchema(d: AttrDraft): AttributeSchema {
  const type = d.required ? d.base : `${d.base}?`;
  const options =
    d.base === "enum"
      ? d.options
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : undefined;
  return {
    name: d.name.trim(),
    label: { nl: d.labelNl.trim(), en: d.labelEn.trim() },
    type,
    ...(options && options.length > 0 ? { options } : {}),
  };
}

export function AttributeBuilder({
  drafts,
  onChange,
}: {
  drafts: AttrDraft[];
  onChange: (next: AttrDraft[]) => void;
}) {
  const { t } = useTranslation();
  const update = (i: number, patch: Partial<AttrDraft>) => {
    onChange(drafts.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));
  };
  const remove = (i: number) => onChange(drafts.filter((_, idx) => idx !== i));
  const add = () =>
    onChange([
      ...drafts,
      { name: "", labelNl: "", labelEn: "", base: "string", required: true, options: "" },
    ]);

  return (
    <div className="sm:col-span-2">
      <span className={ld.label}>{t("settings.subtypes.attrsLabel")}</span>
      <p className={`${ld.hint} mb-2`}>{t("settings.subtypes.attrsHint")}</p>
      <div className="flex flex-col gap-2">
        {drafts.map((d, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: rijen zijn positioneel, geen stabiele id
          <div key={i} className="rounded-md border border-(--ld-border) bg-(--ld-surface) p-3">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <input
                type="text"
                value={d.name}
                onChange={(e) => update(i, { name: e.target.value })}
                placeholder={t("settings.subtypes.attrName")}
                aria-label={t("settings.subtypes.attrName")}
                className={`${ld.inputInline} font-mono text-[13px]`}
              />
              <input
                type="text"
                value={d.labelNl}
                onChange={(e) => update(i, { labelNl: e.target.value })}
                placeholder={t("settings.subtypes.attrLabelNl")}
                aria-label={t("settings.subtypes.attrLabelNl")}
                className={`${ld.inputInline} text-[13px]`}
              />
              <input
                type="text"
                value={d.labelEn}
                onChange={(e) => update(i, { labelEn: e.target.value })}
                placeholder={t("settings.subtypes.attrLabelEn")}
                aria-label={t("settings.subtypes.attrLabelEn")}
                className={`${ld.inputInline} text-[13px]`}
              />
              <select
                value={d.base}
                onChange={(e) => update(i, { base: e.target.value as AttrBase })}
                className={`${ld.inputInline} text-[13px]`}
              >
                {ATTR_BASE_TYPES.map((bt) => (
                  <option key={bt} value={bt}>
                    {t(`settings.subtypes.attrType.${bt}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-[13px]">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-(--ld-primary)"
                  checked={d.required}
                  onChange={(e) => update(i, { required: e.target.checked })}
                />
                {t("settings.subtypes.attrRequired")}
              </label>
              {d.base === "enum" && (
                <input
                  type="text"
                  value={d.options}
                  onChange={(e) => update(i, { options: e.target.value })}
                  placeholder={t("settings.subtypes.attrOptions")}
                  aria-label={t("settings.subtypes.attrOptions")}
                  className={`${ld.inputInline} flex-1 text-[13px]`}
                />
              )}
              <button
                type="button"
                onClick={() => remove(i)}
                className={`ml-auto ${ld.textDanger}`}
              >
                {t("settings.subtypes.attrRemove")}
              </button>
            </div>
          </div>
        ))}
      </div>
      <button type="button" onClick={add} className={`mt-2 ${ld.secondarySm}`}>
        {t("settings.subtypes.attrAdd")}
      </button>
    </div>
  );
}
